import axios from "axios";
import { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import qs from "qs";
import { generateDeviceId } from "@/_helpers/devices";
import { resetPersistedStores } from "@/_helpers/workspace";
import router from "@/_helpers/router";
import { AccountErrors } from "@/constants/account";
import type { RequestError } from "@/types";
import { useAccountStore } from "@/_store/account.module";
import type { App } from "vue";
import { type ErrorPayload, useErrorsStore } from "@/_store/errors.module";
import { RouteName } from "@/constants/routes.ts";
import { i18n } from "@/plugins/i18n.ts";
import api from "@/_helpers/api.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { useDeviceSessionStore } from "@/_store/current-session.module.ts";
import { isPlainObject } from "lodash";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
let refreshTokenPromise: Promise<unknown> | null = null;
let logoutPromise: Promise<unknown> | null = null;

interface NonHandlableEndpoint {
  url: string;
  exactMatch?: boolean;
  includes?: boolean;
}

export const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  paramsSerializer: {
    serialize: (params) => {
      return qs.stringify(params, { arrayFormat: "brackets", skipNulls: true });
    },
  },
  baseURL: import.meta.env.VITE_API_HOST,
});

async function updateRefreshToken() {
  const accountStore = useAccountStore();
  if (!refreshTokenPromise) {
    refreshTokenPromise = accountStore
      .getRefreshToken()
      .then((response) => {
        const newToken = response.data.accessToken;
        accountStore.setToken(newToken);
        return newToken;
      })
      .catch(async (err) => {
        await logoutAfterTokenExpiration();
        throw err; // Handle logout or token expiration here
      })
      .finally(() => {
        refreshTokenPromise = null; // Reset the promise
      });
  }
  return refreshTokenPromise;
}

async function logoutAfterTokenExpiration() {
  const accountStore = useAccountStore();
  if (!logoutPromise) {
    // With no active session (unauthenticated deep-link, or the refresh token is
    // already gone) the server /logout just returns 400 and adds RUM noise. In that
    // case skip the network call and only clear local state + route to login.
    const endServerSession = accountStore.logged
      ? axiosInstance.request(api.logout()).catch((err) => {
          console.error(err);
          throw err; // Handle logout or token expiration here
        })
      : Promise.resolve();
    logoutPromise = endServerSession.finally(async () => {
      accountStore.$reset();
      resetPersistedStores();
      useDialogsStore().closeDialog();
      localStorage.setItem("logout-event", `logout${Math.random()}`);
      await router.push({ name: RouteName.LOGIN }).catch(() => {});
      logoutPromise = null; // Reset the promise (guard push so this always runs)
    });
  }
  return logoutPromise;
}

const handle401Error = async (
  error: AxiosError<RequestError>,
  originalRequest: InternalAxiosRequestConfig
) => {
  const responseUrl = error.response?.config?.url ?? "";
  const authenticationEndpoints = ["/refreshToken", "/auth/transfer", "/auth/exchange"];
  if (authenticationEndpoints.includes(responseUrl)) throw error;

  try {
    // Refresh the token
    await updateRefreshToken();
    // Update the Authorization header with the new token
    originalRequest.headers["Authorization"] = `Bearer ${useAccountStore().token}`;
    // Retry the original request
    return await axiosInstance.request(originalRequest);
  } catch (err: unknown) {
    if (
      responseUrl !== "/admin-users/joinWorkspace" &&
      err instanceof AxiosError &&
      err.status === 401
    ) {
      await logoutAfterTokenExpiration();
    }
    throw err;
  }
};

const handleMFAError = () => {
  router.push({ name: RouteName.MFA_PAGE }).catch(() => {});
};

const shouldHandleError = (responseUrl: string, nonHandleableEndpoints: NonHandlableEndpoint[]) => {
  return !nonHandleableEndpoints.some((endpoint) => {
    if (endpoint.exactMatch) {
      return responseUrl === endpoint.url;
    }

    if (endpoint.includes) {
      return responseUrl.includes(endpoint.url);
    }

    return responseUrl.startsWith(endpoint.url);
  });
};

const handleGeneralError = (
  error: AxiosError<RequestError>,
  nonHandleableEndpoints: NonHandlableEndpoint[]
) => {
  const responseUrl = error.response?.config?.url ?? "";
  if (shouldHandleError(responseUrl, nonHandleableEndpoints)) {
    handleError(error);
  }
  return Promise.reject(error);
};

function handleError(error: AxiosError<RequestError>) {
  if (error.isAxiosError && error.code === AxiosError.ERR_CANCELED) {
    return; // Ignore canceled requests
  }

  const errorsStore = useErrorsStore();
  const response = error.response;
  const data = response?.data;
  const errors = data?.errors;

  const payload: ErrorPayload = {
    message: i18n.global.t("errors.somethingWentWrong", {
      url: response?.config?.url ?? "URL",
      status: response?.status ?? i18n.global.t("general.unknown"),
    }),
    showDialog: false,
    customErrorHandling: false,
  };

  if (errors?.length) {
    const isCustomErrorHandling = (_err: string[] | ErrorPayload[]): _err is ErrorPayload[] => {
      return error.status === 400 && isPlainObject(errors[0]);
    };

    payload.message = isCustomErrorHandling(errors) ? errors[0].message : errors[0];
    payload.showDialog = true;
    payload.customErrorHandling = isCustomErrorHandling(errors);

    if (isCustomErrorHandling(errors)) {
      payload.field = errors[0].field || undefined;
    }
  }

  errorsStore.push(payload);
}

const onRequest = (config: InternalAxiosRequestConfig) => {
  // perform a task before the request is sent
  const accountStore = useAccountStore();
  const workspaceStore = useWorkspaceStore();
  const currentSessionStore = useDeviceSessionStore();
  const token = accountStore.token;
  const workspaceId = workspaceStore.workspace.workspaceId;

  if (config?.url?.includes("/login") || config?.url?.includes("/signup")) {
    if (!currentSessionStore.deviceId) {
      currentSessionStore.setDeviceId(generateDeviceId());
    }

    config.headers["X-Header-DeviceId"] = currentSessionStore.deviceId;
  }

  if (token) {
    let bearerValue;
    switch (config.url) {
      case "/logout":
      case "/refreshToken":
        bearerValue = `Bearer ${accountStore.refreshToken}`;
        break;
      default:
        bearerValue = `Bearer ${token}`;
        break;
    }
    if (!config?.url?.startsWith("/v2/workspace") && workspaceId) {
      config.headers["Workspace"] = config.headers["Workspace"] || workspaceId;
    }
    config.headers["Authorization"] = bearerValue;
  }
  config.headers["Accept-Language"] = accountStore.displayLanguage;
  config.headers["X-Timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return config;
};

const onResponse = (response: AxiosResponse) => {
  return response;
};

const onError = async (error: AxiosError<RequestError>) => {
  // endpoints in which we should not show error modal
  const nonHandleableEndpoints = [
    { url: "/login" },
    { url: "/resetPassword" },
    { url: "/forgotPassword" },
    { url: "/logout" },
    { url: "/refreshToken" },
    { url: "/services/box/configUpload" },
    { url: "/branding" },
    { url: "/devices/ssh/" },
    { url: "/msp/devices/ssh" },
    { url: "/tickets/" },
    { url: "/mobile/activate" },
    { url: "/mobile/resendInvite" },
    { url: "/msp/promo-codes" },
    { url: "/sign-up/verify-email" },
    { url: "/desktop/activate-email" },
    { url: "/sign-up/request-verification-code" },
    { url: "/sign-up/complete" },
    { url: "/dashboard/services/heartbeat" },
    { url: "/security-awareness/course-preview" },
    { url: "/msp/hierarchy", exactMatch: true },
    { url: "/testProxy", includes: true },
    { url: "/settings/swg/urls-file" },
    { url: "/settings/swg/urls/validate-unique" },
    { url: "/protected-users/count", includes: true },
    { url: "/settings/endpointDataGovernance/exclusions/upload" },
    { url: "/settings/devices/allowBlockList/upload" },
    { url: "/settings/phishing/list/csv", exactMatch: true },
    { url: "/backoffice/email-security/sender-allow-list/csv" },
    { url: "/msp/settings/allow-block-list/email-security/csv" },
    { url: "/msp/settings/allow-block-list/endpoint-security/csv" },
    { url: "/users/csv", exactMatch: true },
    // The board is behind a rollout flag, so a 404 from any of these is the expected answer on an
    // environment that does not serve it - the store reads that as "hide the board" and there is nothing
    // for the user to act on.
    { url: "/settings/addons/network/analytics/" },
  ];
  const response = error.response;
  const errorStatus = error?.response?.status;
  const originalRequest = error.config as InternalAxiosRequestConfig;
  const errorCode = response?.data?.errorCode ?? "";

  if (errorStatus === 401) {
    return await handle401Error(error, originalRequest);
  } else if (errorCode === AccountErrors.MFA_REQUIRED) {
    handleMFAError();
  } else {
    return handleGeneralError(error, nonHandleableEndpoints);
  }

  return Promise.reject(error);
};

export default {
  install: (app: App): void => {
    app.config.globalProperties.$http = axiosInstance;

    axiosInstance.interceptors.response.use(onResponse, onError);
    axiosInstance.interceptors.request.use(onRequest);
  },
};
